import {
  AfterViewInit,
  Component,
  ChangeDetectionStrategy,
  ElementRef,
  Input,
  OnDestroy,
  OnInit,
  OnChanges,
  Optional,
  Renderer2,
  SimpleChanges,
  ViewChild,
} from "@angular/core";
import { IStream } from "@auvious/rtc";
import { TileComponent } from "../tile/tile.component";
import { Subscription, merge } from "rxjs";
import { filter } from "rxjs/operators";
import { AudioVisualizerEffect } from "@auvious/media-tools";
import { debugError } from "../../../core-ui/services";
import { ConferenceService } from "../../services";
import { LocalMediaService } from "../../services/local.media.service";

@Component({
  selector: "app-audio-bubble",
  templateUrl: "./audio-bubble.component.html",
  styleUrls: ["./audio-bubble.component.scss"],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AudioBubbleComponent
  implements OnInit, AfterViewInit, OnDestroy, OnChanges
{
  @Input() streamId: string;

  private readonly colorPrimary = "#4ca2ff";
  private readonly colorSecondary = "#ff00ff";

  private visualizer: AudioVisualizerEffect | null = null;
  private subscriptions = new Subscription();
  private resizeObserver: ResizeObserver | null = null;
  private resizeRaf = 0;

  @ViewChild("canvas") canvasRef: ElementRef<HTMLCanvasElement>;

  constructor(
    private renderer: Renderer2,
    private host: ElementRef<HTMLDivElement>,
    private conference: ConferenceService,
    private local: LocalMediaService,
    @Optional() private tile: TileComponent
  ) {}

  ngOnInit(): void {
    this.subscriptions.add(
      this.tile?.viewSizeChanged.subscribe((value) => {
        if (value?.width > 1 && value?.height > 1) {
          this.renderer.setStyle(
            this.host.nativeElement,
            "width",
            `${value.width}px`
          );
          this.renderer.setStyle(
            this.host.nativeElement,
            "height",
            `${value.height}px`
          );
        }
        this.ensureVisualizer();
        this.refreshStreamBinding();
        this.syncVisualizerCanvas();
      })
    );
  }
  ngAfterViewInit(): void {
    this.ensureVisualizer();
    this.refreshStreamBinding();
    this.registerConferenceListeners();
    this.resizeCanvas();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes["streamId"] && !changes["streamId"].firstChange) {
      this.refreshStreamBinding();
    }
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
    cancelAnimationFrame(this.resizeRaf);
    this.resizeObserver?.disconnect();
    this.visualizer?.reset();
    this.visualizer?.dispose();
  }

  private ensureVisualizer() {
    const canvasReady = !!this.canvasRef?.nativeElement;

    if (this.visualizer || !canvasReady) {
      return;
    }

    try {
      this.visualizer = new AudioVisualizerEffect({
        canvas: this.canvasRef.nativeElement,
        colorPrimary: this.colorPrimary,
        colorSecondary: this.colorSecondary,
      });
      // ensure initial sizing with current dimensions
      this.syncVisualizerCanvas();
    } catch (ex) {
      debugError(ex);
      this.visualizer = null;
    }
  }

  /**
   * Adjusts the canvas internal resolution to match display size.
   * This is critical for crisp rendering on high DPI screens.
   */
  private resizeCanvas() {
    if (!this.canvasRef) return;

    const canvas = this.canvasRef.nativeElement;
    const dpr = 1; // window.devicePixelRatio || 1;

    const wrapper = canvas.parentElement as HTMLElement | null;
    const rect =
      wrapper?.getBoundingClientRect() ||
      this.host.nativeElement.getBoundingClientRect() ||
      (canvas as HTMLElement).getBoundingClientRect();

    const width = Math.floor(rect?.width || 0);
    const height = Math.floor(rect?.height || 0);

    if (width <= 1 || height <= 1) return;

    canvas.width = width * dpr;
    canvas.height = height * dpr;
    this.syncVisualizerCanvas();
  }

  private refreshStreamBinding() {
    this.ensureVisualizer();

    if (!this.visualizer || !this.streamId) {
      this.visualizer?.reset();
      return;
    }

    const mediaStream = this.getStream()?.mediaStream;

    if (!mediaStream || !mediaStream.getAudioTracks().length) {
      this.visualizer?.reset();
      return;
    }

    try {
      this.visualizer.setInput(mediaStream);
      // this.resizeCanvasSoon();
      this.syncVisualizerCanvas();
    } catch (ex) {
      debugError(ex);
      this.visualizer.reset();
    }
  }

  private getStream(): IStream | null {
    try {
      return this.streamId ? this.conference.getStream(this.streamId) : null;
    } catch (ex) {
      debugError(ex);
      return null;
    }
  }

  private registerConferenceListeners() {
    this.subscriptions.add(
      this.conference.streamStateChanged$
        .pipe(filter((event) => event.streamId === this.streamId))
        .subscribe(() => this.refreshStreamBinding())
    );

    this.subscriptions.add(
      merge(this.conference.streamMutedChange$, this.local.streamMutedChange$)
        .pipe(filter((event) => event.stream?.id === this.streamId))
        .subscribe(() => this.refreshStreamBinding())
    );
  }

  private syncVisualizerCanvas() {
    const resizeFn = (this.visualizer as any)?.resizeCanvas;
    if (typeof resizeFn === "function") {
      try {
        resizeFn.call(this.visualizer);
      } catch (ex) {
        debugError(ex);
      }
    }
  }
}
